import React, { useMemo } from 'react';
import type { FC } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import { useTheme } from '@mui/material/styles';
import useSettings from '../../hooks/useSettings';
import { THEMES } from '../../constants';

export interface ThemeToggleProps {
  onThemeSelected?: (theme: string) => void;
}

export const ThemeToggle: FC<ThemeToggleProps> = ({ onThemeSelected }) => {
  const theme = useTheme();
  const { settings, saveSettings } = useSettings();

  const selectedTheme = useMemo(() => {
    return settings.theme ?? THEMES.LIGHT;
  }, [settings.theme]);

  const handleThemeChange = (_event: React.MouseEvent<HTMLElement>, nextTheme: string | null): void => {
    if (!nextTheme) {
      return;
    }

    saveSettings({
      ...settings,
      theme: nextTheme
    });

    onThemeSelected?.(nextTheme);
  };

  return (
    <Box
      sx={{
        p: 2
      }}
    >
      <Typography
        color="textPrimary"
        variant="subtitle2"
      >
        Color scheme
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={selectedTheme}
        sx={{
          mt: 1,
          p: 0.5,
          backgroundColor: 'background.default',
          borderRadius: 999,
          '& .MuiToggleButton-root': {
            border: 0,
            borderRadius: 999,
            textTransform: 'none',
            px: 2,
            py: 1,
            fontWeight: 600,
            justifyContent: 'flex-start'
          },
          '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: 'background.paper',
            border: '2px solid',
            borderColor: 'primary.main',
            boxShadow: theme.shadows[1]
          }
        }}
        onChange={handleThemeChange}
      >
        <ToggleButton
          value={THEMES.LIGHT}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <LightModeIcon
              fontSize="small"
            />
            Light
          </Box>
        </ToggleButton>
        <ToggleButton
          value={THEMES.DARK}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <DarkModeIcon
              fontSize="small"
            />
            Dark
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default ThemeToggle;
